import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Gift, Award, Key, Trash2, AlertTriangle, Shield, ShieldOff, 
  Phone, Mail, MapPin, Calendar, Users, QrCode, Copy, Check, Sparkles,
  Wine, Leaf, Cigarette, CreditCard, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useCustomer } from '../context/CustomerContext';
import LoyaltyCard from '../components/LoyaltyCard';
import SurpriseGiftModal from '../components/SurpriseGiftModal';
import { DownloadConsentButton } from '../components/ParentalConsentModal';

const LOYALTY_TIERS = {
  bronze: { name: 'Bronze', color: '#CD7F32', min: 0 },
  silver: { name: 'Argent', color: '#C0C0C0', min: 3 },
  gold: { name: 'Or', color: '#FFD700', min: 6 },
  platinum: { name: 'Platine', color: '#E5E4E2', min: 10 },
  diamond: { name: 'Diamant', color: '#B9F2FF', min: 15 }
};

function getLoyaltyTier(completedCards) {
  if (completedCards >= 15) return LOYALTY_TIERS.diamond;
  if (completedCards >= 10) return LOYALTY_TIERS.platinum;
  if (completedCards >= 6) return LOYALTY_TIERS.gold;
  if (completedCards >= 3) return LOYALTY_TIERS.silver;
  return LOYALTY_TIERS.bronze;
}

export default function CustomerDashboard() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { activeCustomer, setActiveCustomer, activeCards, setActiveCards, isAdmin } = useCustomer();
  const [isLoading, setIsLoading] = useState(true);
  const [showSurpriseGift, setShowSurpriseGift] = useState(false);
  const [showResetPinModal, setShowResetPinModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [totpStatus, setTotpStatus] = useState(null);
  const [copiedQR, setCopiedQR] = useState(false);
  const [debtInfo, setDebtInfo] = useState(null); // Info dette du client

  useEffect(() => {
    loadCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const loadCustomerData = async () => {
    try {
      const [customer, cards] = await Promise.all([
        api.getCustomer(customerId),
        api.getCustomerCards(customerId)
      ]);
      setActiveCustomer(customer);
      setActiveCards(cards);
      
      try {
        const status = await api.getTOTPStatus(customerId);
        setTotpStatus(status);
      } catch (e) {
        setTotpStatus({ enabled: false });
      }
      
      // Vérifier si le client a une dette
      try {
        const debt = await api.checkCustomerDebt(customerId);
        setDebtInfo(debt);
      } catch (e) {
        setDebtInfo({ hasDebt: false, balance: 0 });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate(isAdmin ? '/team-lpe/admin' : '/team-lpe/search');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Désactiver le 2FA pour ce client ?')) return;
    try {
      await api.disableTOTP(customerId);
      setTotpStatus({ enabled: false, verified: false });
      toast.success('2FA désactivé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleStamp = async (cardId, boxIndex, currentValue, vendorCode = null) => {
    try {
      const updatedCard = await api.stampCard(cardId, boxIndex, currentValue, vendorCode);
      setActiveCards(activeCards.map(card => card.id === cardId ? updatedCard : card));
      if (currentValue) toast.success('Tampon ajouté !');
    } catch (error) {
      throw error;
    }
  };

  const handleRedeem = async (cardId, cardType, lineIndex = null) => {
    try {
      const result = await api.redeemCard(cardId, lineIndex);
      if (result.surpriseGift) setShowSurpriseGift(true);
      await loadCustomerData();
      toast.success(cardType === 'puffs' ? 'Puff gratuit !' : 'Cadeau offert !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPin = async () => {
    if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      toast.error('PIN à 4 chiffres requis');
      return;
    }
    try {
      await api.resetCustomerPin(customerId, newPin);
      toast.success('PIN réinitialisé !');
      setShowResetPinModal(false);
      setNewPin('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!adminCode || adminCode.length !== 6) {
      toast.error('Code admin requis');
      return;
    }
    setIsDeleting(true);
    try {
      await api.deleteCustomer(customerId, adminCode);
      toast.success('Client supprimé');
      navigate(isAdmin ? '/team-lpe/admin' : '/team-lpe/search');
    } catch (error) {
      error.response?.status === 401 ? toast.error('Code incorrect') : toast.error('Erreur');
    } finally {
      setIsDeleting(false);
      setAdminCode('');
    }
  };

  const copyQRCode = () => {
    if (activeCustomer?.qrCode) {
      navigator.clipboard.writeText(activeCustomer.qrCode);
      setCopiedQR(true);
      setTimeout(() => setCopiedQR(false), 2000);
      toast.success('QR code copié');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto flex items-center justify-center" style={{ background: '#05050A' }}>
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const loyaltyTier = getLoyaltyTier(activeCustomer?.completedCardsCount || 0);
  
  // Filtrer les cartes non complétées et prendre la carte active (avec le plus de tampons) de chaque type
  const getActiveCardByType = (type) => {
    const cardsOfType = activeCards
      .filter(c => c.type === type && !c.isCompleted)
      .sort((a, b) => {
        const aStamps = a.boxes.filter(b => b).length;
        const bStamps = b.boxes.filter(b => b).length;
        return bStamps - aStamps; // Plus de tampons = priorité
      });
    return cardsOfType[0] || null;
  };
  
  const alcoholCard = getActiveCardByType('alcohol');
  const cbdCard = getActiveCardByType('cbd');
  const puffsCard = getActiveCardByType('puffs');

  // Calculer les tampons actifs
  const getActiveStamps = (card) => card ? card.boxes.filter(b => b).length : 0;

  // Trier les cartes : actives (avec tampons) en premier, inactives en bas
  const sortedCards = [alcoholCard, cbdCard, puffsCard]
    .filter(Boolean)
    .sort((a, b) => {
      const aStamps = getActiveStamps(a);
      const bStamps = getActiveStamps(b);
      // Cartes avec tampons en premier
      if (aStamps > 0 && bStamps === 0) return -1;
      if (aStamps === 0 && bStamps > 0) return 1;
      // Parmi les actives, trier par nombre de tampons (plus de tampons = plus haut)
      return bStamps - aStamps;
    });

  // Données pour le résumé avec tri
  const cardsSummary = [
    { card: alcoholCard, type: 'alcohol', label: 'Alcool', icon: Wine, color: '#EF4444', max: 10 },
    { card: cbdCard, type: 'cbd', label: 'CBD', icon: Leaf, color: '#10B981', max: 10 },
    { card: puffsCard, type: 'puffs', label: 'Puffs', icon: Cigarette, color: '#A855F7', max: 40 },
  ].sort((a, b) => {
    const aStamps = getActiveStamps(a.card);
    const bStamps = getActiveStamps(b.card);
    if (aStamps > 0 && bStamps === 0) return -1;
    if (aStamps === 0 && bStamps > 0) return 1;
    return bStamps - aStamps;
  });

  return (
    <div className="min-h-screen" style={{ background: '#05050A' }}>
      {/* Ambient */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-fuchsia-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header sticky */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate(isAdmin ? '/team-lpe/admin' : '/team-lpe/search')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Retour</span>
            </Button>
            
            {/* Bouton Nouveau Client - Accès rapide */}
            <Button
              onClick={() => navigate('/team-lpe/search?new=1')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm"
            >
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Nouveau</span>
              <span className="sm:hidden">+</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Loyalty Badge */}
            <div 
              className="px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2"
              style={{ 
                background: `${loyaltyTier.color}20`,
                color: loyaltyTier.color,
                border: `1px solid ${loyaltyTier.color}50`
              }}
            >
              <Award className="w-4 h-4" />
              {loyaltyTier.name}
            </div>
            
            {/* 2FA Badge */}
            {totpStatus?.enabled && (
              <div className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium flex items-center gap-1 border border-emerald-500/30">
                <Shield className="w-3 h-3" /> 2FA
              </div>
            )}
            
            {/* Badge Dette */}
            {debtInfo?.hasDebt ? (
              <div className="px-3 py-1.5 rounded-full bg-red-500/20 text-red-400 text-xs font-bold flex items-center gap-1 border border-red-500/50 animate-pulse">
                <CreditCard className="w-3 h-3" />
                {debtInfo.balance.toFixed(0)}€ dû
              </div>
            ) : debtInfo && !debtInfo.hasDebt && (
              <div className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium flex items-center gap-1 border border-emerald-500/30">
                <Check className="w-3 h-3" /> À jour
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="backdrop-blur-xl bg-black/40 rounded-3xl border border-white/10 overflow-hidden">
            {/* Top gradient bar */}
            <div className="h-1" style={{ background: `linear-gradient(90deg, ${loyaltyTier.color} 0%, #00F0FF 50%, ${loyaltyTier.color} 100%)` }} />
            
            <div className="p-6 sm:p-8">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left: Avatar + Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-5">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {activeCustomer?.profilePhoto ? (
                        <img 
                          src={activeCustomer.profilePhoto} 
                          alt={activeCustomer.name}
                          className="w-24 h-24 rounded-2xl object-cover border-2"
                          style={{ borderColor: loyaltyTier.color }}
                        />
                      ) : (
                        <div 
                          className="w-24 h-24 rounded-2xl flex items-center justify-center text-4xl border-2"
                          style={{ background: '#0F0F16', borderColor: loyaltyTier.color }}
                        >
                          {activeCustomer?.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      {activeCustomer?.isMinor && (
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                          <AlertTriangle className="w-4 h-4 text-black" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight truncate">
                        {activeCustomer?.name}
                      </h1>
                      
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Phone className="w-4 h-4 text-cyan-400" />
                          <span className="font-mono">{activeCustomer?.phone}</span>
                        </div>
                        {activeCustomer?.email && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Mail className="w-4 h-4 text-fuchsia-400" />
                            <span className="truncate">{activeCustomer.email}</span>
                          </div>
                        )}
                        {activeCustomer?.birthDate && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Calendar className="w-4 h-4 text-violet-400" />
                            <span>{new Date(activeCustomer.birthDate).toLocaleDateString('fr-FR')}</span>
                            {activeCustomer.isMinor && <span className="text-amber-400 text-xs font-semibold">(Mineur)</span>}
                          </div>
                        )}
                        {(activeCustomer?.address || activeCustomer?.city) && (
                          <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate">{activeCustomer.address} {activeCustomer.postalCode} {activeCustomer.city}</span>
                          </div>
                        )}
                      </div>

                      {/* Social badges */}
                      {(activeCustomer?.snapchat || activeCustomer?.instagram) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {activeCustomer.snapchat && (
                            <span className="px-2 py-1 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs">👻 {activeCustomer.snapchat}</span>
                          )}
                          {activeCustomer.instagram && (
                            <span className="px-2 py-1 rounded-lg bg-pink-500/10 text-pink-400 text-xs">📸 {activeCustomer.instagram}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <Button
                      onClick={() => setShowResetPinModal(true)}
                      size="sm"
                      className="bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                    >
                      <Key className="w-4 h-4 mr-1" /> PIN
                    </Button>
                    {totpStatus?.enabled && (
                      <Button
                        onClick={handleDisable2FA}
                        size="sm"
                        className="bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20"
                        title="Désactiver le 2FA (perte de téléphone)"
                      >
                        <ShieldOff className="w-4 h-4 mr-1" /> 2FA Off
                      </Button>
                    )}
                    <Button
                      onClick={copyQRCode}
                      size="sm"
                      className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
                    >
                      {copiedQR ? <Check className="w-4 h-4 mr-1" /> : <QrCode className="w-4 h-4 mr-1" />}
                      QR
                    </Button>
                    <Button
                      onClick={() => setShowDeleteModal(true)}
                      size="sm"
                      className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>

                {/* Right: Stats Cards */}
                <div className="flex flex-row lg:flex-col gap-4">
                  {/* Cartes complétées */}
                  <div 
                    className="flex-1 lg:w-48 p-5 rounded-2xl text-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${loyaltyTier.color}20 0%, ${loyaltyTier.color}05 100%)`,
                      border: `1px solid ${loyaltyTier.color}30`
                    }}
                  >
                    <div className="text-4xl font-mono font-bold text-white">{activeCustomer?.completedCardsCount || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">Cartes complétées</div>
                    {activeCustomer?.completedCardsCount > 0 && activeCustomer.completedCardsCount % 3 === 0 && (
                      <div className="mt-2 flex items-center justify-center gap-1 text-fuchsia-400 text-xs">
                        <Gift className="w-3 h-3" /> Surprise !
                      </div>
                    )}
                  </div>
                  
                  {/* Filleuls */}
                  <div className="flex-1 lg:w-48 p-5 rounded-2xl bg-violet-500/10 border border-violet-500/20 text-center">
                    <div className="text-4xl font-mono font-bold text-white">{activeCustomer?.referralCount || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">Filleuls</div>
                  </div>
                </div>
              </div>

              {/* Minor Alert */}
              {activeCustomer?.isMinor && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-5 rounded-2xl bg-amber-500/10 border border-amber-500/30"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-amber-400 font-bold">Client Mineur</h3>
                      {(activeCustomer.guardianFirstName || activeCustomer.guardianLastName) ? (
                        <div className="mt-2">
                          <p className="text-slate-400 text-sm">Responsable légal :</p>
                          <p className="text-white">{activeCustomer.guardianFirstName} {activeCustomer.guardianLastName}</p>
                          {activeCustomer.guardianPhone && (
                            <p className="text-cyan-400 text-sm font-mono">{activeCustomer.guardianPhone}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-red-400 text-sm mt-1">Aucun responsable enregistré</p>
                      )}
                      
                      {/* Consent status */}
                      <div className={`mt-3 p-3 rounded-xl ${activeCustomer.parentalConsentSigned ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                        {activeCustomer.parentalConsentSigned ? (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-emerald-400 text-sm font-medium">✓ Autorisation signée</p>
                              <p className="text-slate-500 text-xs">{activeCustomer.parentalConsentSignerName}</p>
                            </div>
                            {isAdmin && <DownloadConsentButton customer={activeCustomer} />}
                          </div>
                        ) : (
                          <p className="text-red-400 text-sm">✗ Autorisation non signée</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Loyalty Cards Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-8"
        >
          {cardsSummary.map(({ card, type, label, icon: Icon, color, max }) => {
            const stamps = getActiveStamps(card);
            const isActive = stamps > 0;
            return (
              <div 
                key={type}
                className={`p-4 rounded-2xl backdrop-blur-md transition-all ${
                  isActive 
                    ? 'bg-black/40 border border-white/20 hover:border-white/30' 
                    : 'bg-black/20 border border-white/5 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center" 
                    style={{ background: isActive ? `${color}20` : 'rgba(255,255,255,0.05)' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: isActive ? color : '#666' }} />
                  </div>
                  <div>
                    <div className={`font-semibold ${isActive ? 'text-white' : 'text-gray-500'}`}>{label}</div>
                    <div className="text-xs text-slate-500">{stamps}/{max} tampons</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${(stamps / max) * 100}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: isActive ? color : '#444' }}
                  />
                </div>
                {!isActive && (
                  <div className="text-xs text-gray-600 text-center mt-2">Inactive</div>
                )}
              </div>
            );
          })}
        </motion.div>

        {/* Cards Grid - Actives en premier */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          {sortedCards.map((card) => (
            <LoyaltyCard 
              key={card.id} 
              card={card} 
              onStamp={handleStamp} 
              onRedeem={handleRedeem}
              isInactive={getActiveStamps(card) === 0}
            />
          ))}
        </motion.div>
      </main>

      {/* Modals */}
      <SurpriseGiftModal isOpen={showSurpriseGift} onClose={() => setShowSurpriseGift(false)} />

      {/* Reset PIN Modal */}
      <Dialog open={showResetPinModal} onOpenChange={setShowResetPinModal}>
        <DialogContent className="bg-[#0F0F16] border-amber-500/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <Key className="w-5 h-5" /> Nouveau PIN
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">Pour <span className="text-white font-medium">{activeCustomer?.name}</span></p>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="0000"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              className="bg-white/5 border-white/10 text-white text-center text-3xl font-mono tracking-[0.5em] h-16"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setShowResetPinModal(false); setNewPin(''); }} className="flex-1 border-white/10">Annuler</Button>
              <Button onClick={handleResetPin} disabled={newPin.length !== 4} className="flex-1 bg-amber-500 text-black hover:bg-amber-400">Confirmer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#0F0F16] border-red-500/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Supprimer
            </DialogTitle>
            <DialogDescription className="text-slate-500">Action irréversible</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-white font-bold">{activeCustomer?.name}</p>
              <p className="text-slate-500 text-sm font-mono">{activeCustomer?.phone}</p>
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Code admin (6 chiffres)</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value.replace(/\D/g, ''))}
                className="mt-2 bg-white/5 border-white/10 text-white text-center text-2xl font-mono tracking-[0.3em]"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setShowDeleteModal(false); setAdminCode(''); }} className="flex-1 border-white/10">Annuler</Button>
              <Button onClick={handleDeleteCustomer} disabled={isDeleting || adminCode.length !== 6} className="flex-1 bg-red-500 text-white hover:bg-red-400">
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
